import { useState } from 'react';
import { Box, Button, Flex, Grid, GridItem, Stack } from '@chakra-ui/react';
import { AxiosError } from 'axios';

import { fetchAPI } from '@/api/fetch';
import { apiCreateJob, apiUploadJD, CreateJobPayload } from '@/api/services';
import { useCreateJob } from '@/store/createJob';
import { Stepper, getEmploymentTypeLabel, getWorkTypeLabel } from '@/types/enums';
import { Job } from '@/types/Job';
import RichEditor from '@/components/forms/RichEditor';

export default function Review() {
  const createJobStore = useCreateJob();
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<boolean>(false);

  const navigateToStep = (step: Stepper) => {
    createJobStore.setStep(step);
    createJobStore.setCurrentPage(step);
  };

  const formatCurrency = (value: number): string => (value ? `$${value.toLocaleString()}` : '');

  const handleBack = () => navigateToStep(Stepper.AiAnalysis);

  const handleSubmit = async () => {
    setSubmitting(true);
    setSubmitError(false);

    const parseSalary = (salary: string) => Number(salary.replace(/[$,]/g, ''));
    const salaryFromValue = createJobStore.salaryFrom
      ? parseSalary(createJobStore.salaryFrom.toString())
      : null;
    const salaryToValue = createJobStore.salaryTo
      ? parseSalary(createJobStore.salaryTo.toString())
      : null;

    try {
      const jobPayload: CreateJobPayload = {
        client_id: createJobStore.selectedClient!.client_id,
        job_title: createJobStore.jobTitle,
        category: createJobStore.category,
        job_description: createJobStore.jobDescription,
        currency: createJobStore.currency!.code,
        salary_from: salaryFromValue,
        salary_to: salaryToValue,
        employment_type: createJobStore.employmentType!,
        work_type: createJobStore.workType!,
        location: createJobStore.location,
        contact_name: createJobStore.contactName,
        contact_role: createJobStore.contactRole,
        contact_email: createJobStore.contactEmail,
        contact_phone: createJobStore.contactPhone,
        requirements: {
          requirements: createJobStore.requirements,
          nice_to_haves: createJobStore.niceToHave
        },
        position_tags: createJobStore.position_tags
      };

      const response = await fetchAPI<Job | AxiosError>({
        request: () => apiCreateJob({ payload: jobPayload })
      });

      if (response.success) {
        const data = response.data as Job;
        if (createJobStore.jdFile) {
          await fetchAPI<void>({
            request: () =>
              apiUploadJD({
                jobId: data.job_id,
                payload: { file: createJobStore.jdFile! }
              })
          });
        }

        createJobStore.setNewJob(data);
        navigateToStep(Stepper.Success);
      } else {
        console.error('Failed to create job:', response.message);
        setSubmitError(true);
      }
    } catch (error) {
      console.error('Failed to create job:', error);
      setSubmitError(true);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Box
        w="100%"
        border="2px solid"
        borderRadius="12px"
        borderColor="neutrals.300"
        bg="background.secondary"
      >
        <Box display="flex" flexDirection="column" p="32px">
          <Box fontWeight="bold" fontSize="36px" mb="8px">
            Review
          </Box>
          <Box color="neutrals.500" mb="40px">
            Compare the uploaded job description with the company profile and job market data.
          </Box>
          <Box fontWeight="bold" fontSize="24px" mb="24px">
            Job Details
          </Box>
          <Grid templateColumns="repeat(2, 1fr)" gap="24px" mb="24px">
            {[
              { label: 'Job Title', value: createJobStore.jobTitle },
              { label: 'Category', value: createJobStore.category || 'N/A' },
              {
                label: 'Salary',
                value:
                  createJobStore.salaryFrom && createJobStore.salaryTo
                    ? `${createJobStore.currency!.code} ${formatCurrency(
                        createJobStore.salaryFrom
                      )} to ${formatCurrency(createJobStore.salaryTo)}`
                    : 'N/A'
              },
              {
                label: 'Employment Type',
                value: createJobStore.employmentType
                  ? getEmploymentTypeLabel(createJobStore.employmentType)
                  : 'N/A'
              },
              {
                label: 'Work Type',
                value: createJobStore.workType ? getWorkTypeLabel(createJobStore.workType) : 'N/A'
              },
              { label: 'Location', value: createJobStore.location }
            ].map((item, index) => (
              <GridItem key={index}>
                <Box fontWeight="bold" fontSize="16px">
                  {item.label}
                </Box>
                <Box fontSize="16px" color="neutrals.700">
                  {item.value}
                </Box>
              </GridItem>
            ))}
          </Grid>
          <Grid templateColumns="1fr" gap="24px" mb="44px">
            <GridItem>
              <Box fontWeight="bold" fontSize="16px" mb="24px">
                Job Description
              </Box>
              <RichEditor
                key="job-description"
                content={createJobStore.jobDescription}
                type="job-description"
                readOnly
              />
            </GridItem>
          </Grid>
          <Box fontWeight="bold" fontSize="24px" mb="24px">
            Client Contact
          </Box>
          <Grid templateColumns="repeat(2, 1fr)" gap="24px" mb="44px">
            {[
              { label: 'Name', value: createJobStore.contactName },
              { label: 'Role', value: createJobStore.contactRole },
              { label: 'Email', value: createJobStore.contactEmail },
              { label: 'Phone', value: createJobStore.contactPhone }
            ].map((item, index) => (
              <GridItem key={index}>
                <Box fontWeight="bold" fontSize="16px">
                  {item.label}
                </Box>
                <Box fontSize="16px" color="neutrals.700">
                  {item.value || 'N/A'}
                </Box>
              </GridItem>
            ))}
          </Grid>
          <Box fontWeight="bold" fontSize="24px" mb="20px">
            AI Analysis
          </Box>
          {[
            { title: 'Requirements', items: createJobStore.requirements },
            { title: 'Nice To Have', items: createJobStore.niceToHave }
          ].map((section, index) => (
            <Box
              key={index}
              borderRadius="12px"
              border="1px solid"
              borderColor="neutrals.300"
              width="80%"
              mb="20px"
            >
              <Stack>
                <Flex
                  justifyContent="space-between"
                  alignItems="center"
                  bg="neutrals.50"
                  p="16px 32px"
                  borderBottom="1px solid"
                  borderColor="neutrals.300"
                  borderTopRadius="12px"
                >
                  <Box fontWeight="bold" fontSize="18px">
                    {section.title}
                  </Box>
                </Flex>
                {section.items.map((item, idx) => (
                  <Flex
                    key={idx}
                    justifyContent="space-between"
                    alignItems="center"
                    p="10px 16px 16px 16px"
                    borderBottom={idx !== section.items.length - 1 ? '1px solid' : 'none'}
                    borderColor="neutrals.300"
                  >
                    <Box ml="16px">{item}</Box>
                  </Flex>
                ))}
              </Stack>
            </Box>
          ))}
        </Box>
      </Box>
      {submitError && (
        <Box mt="24px" color="error.500" fontSize="16px">
          Failed to create job. Please try again.
        </Box>
      )}
      <Flex justifyContent="flex-end" mt="64px">
        <Button
          w="120px"
          h="48px"
          borderRadius="8px"
          borderColor="primary.900"
          color="primary.900"
          bg="background.secondary"
          fontSize="16px"
          fontWeight="regular"
          mr="16px"
          onClick={handleBack}
          disabled={submitting}
        >
          Back
        </Button>
        <Button
          w="120px"
          h="48px"
          borderRadius="8px"
          bg="primary.900"
          fontSize="16px"
          fontWeight="regular"
          onClick={handleSubmit}
          loading={submitting}
        >
          Submit
        </Button>
      </Flex>
    </>
  );
}
